import React, { useCallback } from 'react'
import {
  Box,
  Select,
  MenuItem,
  Button,
  SelectChangeEvent,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material'
import { INode } from 'shared/types/tree'
import { useNavigateTo, useParams } from 'infrastructure/routing'
import { retrieveIds, useUpdateTreeNode } from './TreeRequests'
import { useSearchParams } from 'shared/hooks'
import { useGetTreeById, useResetTree } from 'shared/hooks/trees/hooks'
import { nodeForId } from '../utility/TreeHelpers'
import { treeManagementToolUrl } from './TreeActions'
import { atom, useAtom } from 'infrastructure/state/jotai.wrapper'

const doingItAtom = atom(false)

export const NodeReorderWrapper = (ids: {
  treeId: string
  versionId: string
}) => {
  const goToNode = useGoToNode(ids)
  const { tree: allTree } = useGetTreeById(ids)
  const [tree, setTree] = React.useState<INode>()

  const [searchParams] = useSearchParams()

  const { NodeId } = useParams()
  const nodeId = NodeId || searchParams.get('nodeId')

  React.useEffect(() => {
    if (!allTree) {
      setTree(undefined)
      return
    } else if (nodeId) {
      setTree(nodeForId(nodeId, allTree))
    } else {
      setTree(allTree)
    }
  }, [allTree, nodeId])

  if (!tree) return <Box>Loading...</Box>
  return (
    <>
      <Button onClick={() => goToNode()}>Start Over</Button>
      {tree.parent_id && (
        <Button onClick={() => goToNode(tree?.parent_id)}>Parent</Button>
      )}
      <NodeReorder nodeWithChildrenToReorder={tree} goToNode={goToNode} />
    </>
  )
}

export function NodeReorder({
  nodeWithChildrenToReorder,
  goToNode,
}: {
  nodeWithChildrenToReorder: INode
  goToNode: (nodeId?: string) => void
}) {
  const resetTree = useResetTree()
  const childNodes = nodeWithChildrenToReorder.nodes

  function onUpdate() {
    const params = {
      treeId: nodeWithChildrenToReorder.tree_id,
      versionId: nodeWithChildrenToReorder.version,
    }
    resetTree(params)
  }

  return (
    <Box>
      <Box>Get The Nodes In Order</Box>
      <Box>
        {nodeWithChildrenToReorder.type}: {nodeWithChildrenToReorder.title}
      </Box>
      <Box padding={'10px'}>
        {childNodes.map((child) => {
          return (
            <SortItem
              key={child.id}
              item={child}
              siblings={childNodes}
              onUpdate={onUpdate}
              goToNode={goToNode}
            />
          )
        })}
      </Box>
    </Box>
  )
}

function SortItem({
  item,
  siblings,
  onUpdate,
  goToNode,
}: {
  item: INode
  siblings: INode[]
  onUpdate: () => void
  goToNode: (arg0?: string) => void
}) {
  const [doingIt, setDoingIt] = useAtom(doingItAtom)
  const updater = useUpdateTreeNode()
  const [filteredSiblings, setFilteredSiblings] = React.useState<INode[]>()
  const [selectedSibling, setSelectedSibling] = React.useState<INode>()
  const [before, setBefore] = React.useState(false)

  React.useEffect(() => {
    setFilteredSiblings(siblings.filter((s) => s.id !== item.id))
  }, [item, siblings])

  function onSelect(e: SelectChangeEvent<any>) {
    setSelectedSibling(filteredSiblings?.find((s) => s.id === e.target.value))
  }

  async function doIt() {
    if (!selectedSibling) return
    setDoingIt(true)
    const prevOrNext = before ? 'next' : 'prev'
    const params = {
      ...retrieveIds(item),
      [`${prevOrNext}_sibling_node_id`]: selectedSibling.id,
    }
    await updater(params)
    onUpdate()
    setDoingIt(false)
  }

  return (
    <Box key={item.id} padding={'10px'} border={'1px solid'}>
      <Box>
        <Button disabled={doingIt} onClick={() => goToNode(item.id)}>
          <i>{item.type}</i>: {item.title}
        </Button>
      </Box>
      <Box>
        <ToggleButtonGroup
          disabled={doingIt}
          exclusive={true}
          size={'small'}
          value={before}
          onChange={(e: any, value: any) => setBefore(value)}
        >
          <ToggleButton value={false}>After</ToggleButton>
          <ToggleButton value={true}>Before</ToggleButton>
        </ToggleButtonGroup>
        <Select
          disabled={doingIt}
          value={selectedSibling?.id || ''}
          onChange={onSelect}
        >
          {siblings.map((s) => {
            return (
              <MenuItem key={s.id} value={s.id}>
                {s.title}
              </MenuItem>
            )
          })}
        </Select>
      </Box>
      <Box>
        <Button disabled={doingIt} onClick={doIt}>
          Do It
        </Button>
      </Box>
    </Box>
  )
}

function useGoToNode({
  treeId,
  versionId,
}: {
  treeId: string
  versionId: string
}) {
  const navigate = useNavigateTo()
  return useCallback(
    (nodeId?: string) => {
      const toolUrl = treeManagementToolUrl({
        treeId,
        versionId,
        nodeId,
        tool: 'Node Reorder',
      })
      navigate(toolUrl)
    },
    [navigate, treeId, versionId],
  )
}
